"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.queueSessionTrackingCookies = exports.updateSessionTrackingCookies = exports.sessionTrackingProperties = void 0;
const js_cookie_1 = __importDefault(require("js-cookie"));
const cookieKeys = {
    sessionStatusKey: 'isLoggedIn',
    previousSessionStatusKey: 'hasLoggedInPreviously',
    landingPageKey: 'session:landingPage',
    rawLandingPageKey: 'session:rawLandingPage',
    landingLocale: 'session:landingLocale',
    metaClickId: '_fbc',
    hubspotToken: 'hubspotutk',
};
// After the user is inactive for this amount of time, we consider them to be in a new session
const SESSION_LENGTH_IN_MINUTES = 30;
const deriveLandingPageFromLocation = () => {
    const host = window.location.host;
    const pathname = window.location.pathname;
    const [locale, ...rawPathParts] = pathname.replace(/^\//, '').split('/');
    const rawPath = rawPathParts.length ? rawPathParts.join('/') : '/';
    return {
        landingPage: host + pathname,
        rawLandingPage: rawPath,
        landingLocale: locale,
    };
};
const sessionTrackingProperties = (landingPageFallback) => {
    const hasCurrentSessionCookie = js_cookie_1.default.get(cookieKeys.sessionStatusKey) === 'true';
    const hasPreviousSessionCookie = js_cookie_1.default.get(cookieKeys.previousSessionStatusKey) === 'true';
    const storedLandingPage = js_cookie_1.default.get(cookieKeys.landingPageKey);
    const storedRawLandingPage = js_cookie_1.default.get(cookieKeys.rawLandingPageKey);
    const storedLandingLocale = js_cookie_1.default.get(cookieKeys.landingLocale);
    const currentLocation = landingPageFallback ?? deriveLandingPageFromLocation();
    // If the cookie exists and hasn't expired, keep the same value.
    // Otherwise, use the supplied fallback (or the current page).
    const landingPage = storedLandingPage || currentLocation.landingPage;
    const rawLandingPage = storedRawLandingPage || currentLocation.rawLandingPage;
    const landingLocale = storedLandingLocale || currentLocation.landingLocale;
    return {
        landingPage,
        rawLandingPage,
        landingLocale,
        loggedInCurrently: hasCurrentSessionCookie,
        loggedInPreviously: hasPreviousSessionCookie || hasCurrentSessionCookie,
        facebookClickId: js_cookie_1.default.get(cookieKeys.metaClickId),
        hubspotutk: js_cookie_1.default.get(cookieKeys.hubspotToken),
    };
};
exports.sessionTrackingProperties = sessionTrackingProperties;
const updateSessionTrackingCookies = (landingPageFallback) => {
    const { landingPage, rawLandingPage, landingLocale, loggedInCurrently } = (0, exports.sessionTrackingProperties)(landingPageFallback);
    const expires = new Date();
    // Set the cookie expiration as `SESSION_LENGTH_IN_MINUTES` from now
    expires.setMinutes(expires.getMinutes() + SESSION_LENGTH_IN_MINUTES);
    const cookiesToSet = [
        { key: cookieKeys.landingPageKey, value: landingPage, domain: 'pleo.io' },
        { key: cookieKeys.rawLandingPageKey, value: rawLandingPage, domain: 'pleo.io' },
        { key: cookieKeys.landingLocale, value: landingLocale },
    ];
    cookiesToSet.forEach(({ key, value, domain }) => {
        js_cookie_1.default.set(key, value, {
            // Reset the session timer on every page load
            expires,
            domain,
        });
    });
    if (loggedInCurrently) {
        js_cookie_1.default.set(cookieKeys.previousSessionStatusKey, 'true', { expires: 365 });
    }
};
exports.updateSessionTrackingCookies = updateSessionTrackingCookies;
let consentListenerRegistered = false;
let capturedLandingPage;
/**
 * Writes session/landing-page attribution cookies immediately once
 * advertising consent is granted, in addition to on every navigation.
 * Without this, a visitor who consents without navigating away (no
 * subsequent route change to re-check consent) would never get their
 * session cookies set until they happened to move to another page.
 *
 * Captures the location from the *first* call this session (before consent
 * may be granted) and reuses it as the landing-page fallback on every later
 * flush. Without this, a visitor who lands on `/a`, browses to `/b`, and only
 * then consents would get `/b` — wherever they happened to be when the
 * consent-change listener fired — recorded as their landing page instead of
 * `/a`.
 *
 * Takes the consent check/subscription as parameters (rather than importing
 * them from `@commercial-website/consent`) to avoid a circular workspace
 * dependency — `packages/consent` already depends on this package.
 */
const queueSessionTrackingCookies = (consent) => {
    if (!capturedLandingPage) {
        capturedLandingPage = deriveLandingPageFromLocation();
    }
    const flushSessionTrackingCookies = () => {
        if (consent.hasAdvertisingConsent()) {
            (0, exports.updateSessionTrackingCookies)(capturedLandingPage);
        }
    };
    if (!consentListenerRegistered) {
        consentListenerRegistered = true;
        consent.onConsentChange(flushSessionTrackingCookies);
    }
    flushSessionTrackingCookies();
};
exports.queueSessionTrackingCookies = queueSessionTrackingCookies;
